import React, { useState, useEffect } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import environment from 'environments';
import apiService from 'app/bff/services/apiService';
import { LinkPositionEnum, LinkAuthenticationEnum } from 'app/bff/enums/link';
import { LoginTypesEnum } from 'app/bff/enums/login';
import { LinkInfoResp, ChildResp } from 'app/bff/models/info/infoGetlinkUrl';
import { ROUTES } from 'app/core/router/paths';
import commonService from 'app/core/services/commonService';
import modalService from 'app/core/services/modalService';
import { ModalNamesEnum } from 'app/core/enums/ui/modals';
import { DeviceTypesEnum } from 'app/core/enums/device';
import { HomeLifeInsuranceMenu, HomeTravelInsuranceMenu } from 'app/core/models/ui/menus';
import { RootState } from 'app/store/types';
import { sendLogoutAction } from 'app/store/features/member/login/actions';
import { SystemParamIdEnum } from 'app/bff/enums/system';
import { DocsIdEnum } from 'app/bff/enums/docs';
import { setIsMobileNavOpenAction } from 'app/store/ui/actions';
import useUIEffect from 'app/core/hooks/useUIEffect';

declare const $: any;

const Header: React.FC = () => {
  const routerLocation = useLocation();
  const routerHistory = useHistory();
  const reduxDispatch = useDispatch();
  const [headerState, setHeaderState] = useState<LinkInfoResp[]>([]);
  const [menuBarActive, setMenuBarActive] = useState<boolean>(false);
  const [sectionActive, setSectionActive] = useState<string>('');
  const [pageSections, setPageSections] = useState<any[]>([]);
  const [mobileNav, setMobileNav] = useState(false);
  const authorizationState = useSelector((state: RootState) => state.common.member.authorization);
  const memberProfileState = useSelector((state: RootState) => state.common.member.profile);
  const commonBaseUrlsState = useSelector((state: RootState) => state.common.baseUrls);
  // const commonExtDocState = useSelector((state: RootState) => state.common.links.extdoc);
  const commonLinkState = useSelector((state: RootState) => state.common.links);
  const commonExtDocState = commonLinkState?.extdoc ?? undefined;
  const isModalShown = useUIEffect();

  /**
   * @description 附加元件相關 Script (JavaScript resource)
   */
  useEffect(() => {
    if (headerState.length > 0) {
      commonService.appendScriptResource(`${environment.application.homepage}/js/menu.js`);
    }
  }, [headerState]);

  /**
   * @description 取得 Header 資料
   */
  useEffect(() => {
    (async () => {
      const response = await apiService.postInfoGetlinkUrl({ page: [LinkPositionEnum.Header] });
      if (response?.header) setHeaderState(response.header);
    })();
  }, []);

  /**
   * @description 根據路由更換頁面區塊之選單
   */
  useEffect(() => {
    switch (routerLocation.pathname) {
      case ROUTES.HOME__TRAVEL_INSURANCE: {
        setPageSections(HomeTravelInsuranceMenu);
        break;
      }
      case ROUTES.HOME__LIFE_INSURANCE: {
        setPageSections(HomeLifeInsuranceMenu);
        break;
      }
      default: {
        setPageSections([]);
      }
    }
  }, [routerLocation.pathname]);

  /**
   * @description 滾動時依當前 section 改變 menu bar 樣式
   */
  useEffect(() => {
    window.addEventListener('scroll', changeMenuItemStyle);
    return () => {
      window.removeEventListener('scroll', changeMenuItemStyle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSections]);

  const changeMenuItemStyle = function () {
    const mobile = window.matchMedia('(max-width: 992px)');
    if (mobile.matches) {
      $('#bar-menu-items-wrapper').slideUp();
    }
    if (!pageSections || pageSections.length === 0) return;
    const windowYOffest = window.scrollY || window.pageYOffset;
    const _sections = pageSections.filter(it => it.target !== 'index-showcase');
    // 取得並紀錄當前滾動所在的區塊
    const currentSection = _sections.reduce((acc, cur) => {
      const accElem = $(`#${acc.target}`);
      const curElem = $(`#${cur.target}`);
      if (!accElem || !curElem) return acc;
      const accOffset = $(`#${acc.target}`).offset();
      const curOffset = $(`#${cur.target}`).offset();
      return Math.abs(curOffset.top - windowYOffest) < Math.abs(accOffset.top - windowYOffest) ? cur : acc;
    });
    if (currentSection) {
      if (sectionActive !== currentSection.target) {
        setSectionActive(currentSection.target);
      }
    } else {
      if (sectionActive) setSectionActive('');
    }
  };

  /**
   * @description 處理視窗滾動控制 Menu 顯示/隱藏
   */
  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 992px)');
    window.addEventListener('scroll', () => {
      if (mobile.matches) {
        // Mobile
        const indexShowcaseSectionMobileMenu = document.getElementById('page-mobile-menu');
        if (indexShowcaseSectionMobileMenu) {
          const active = indexShowcaseSectionMobileMenu.getBoundingClientRect().top < 0;
          setMenuBarActive(active);
        } else {
          setMenuBarActive(false);
        }
      } else {
        // PC
        const indexShowcaseSectionMenu = document.getElementsByClassName('menu')[0];
        if (indexShowcaseSectionMenu) {
          const active = indexShowcaseSectionMenu.getBoundingClientRect().top < 0;
          setMenuBarActive(active);
        } else {
          setMenuBarActive(false);
        }
      }
    });
  }, []);

  /**
   * @description 開啟「會員登入」Modal
   * @param device 裝置
   */
  const handleOpenLoginModal = (device: DeviceTypesEnum) => {
    modalService.trigger(ModalNamesEnum.MemberLoginModal, true);
    // 判斷若為行動裝置，則透過 jQuery 關閉手機版 Navigation
    if (device === DeviceTypesEnum.Mobile) {
      $('#mobile-nav-btn').trigger('click');
    }
  };

  /**
   * @description 處理導航
   * @param device 裝置
   * @param nav 導航資料內容
   */
  const handleNavigation = (device: DeviceTypesEnum, nav: LinkInfoResp | ChildResp) => {
    // 連結需要登入
    if (nav.link && nav.login === LinkAuthenticationEnum.Required && !authorizationState) {
      modalService.login(true, nav.link as ROUTES);
      // 判斷若為行動裝置，則透過 jQuery 關閉手機版 Navigation
      if (device === DeviceTypesEnum.Mobile) {
        $('#mobile-nav-btn').trigger('click');
      }
      return;
    }
    // 連結處理方式
    if (nav.link) {
      // 處理導頁
      // const isExternalURL = nav.link.includes('http');
      // !isExternalURL
      //   ? routerHistory.push(nav.link)
      //   : window.open(nav.link);
      if (nav.target === '1') {
        window.open(nav.link, '_blank');
      } else {
        routerHistory.push(nav.link);
      }
      // 判斷若為行動裝置，則透過 jQuery 關閉手機版 Navigation
      if (device === DeviceTypesEnum.Mobile) {
        $('#mobile-nav-btn').trigger('click');
      }
    }
  };

  /**
   * @description 處理手機版連結導頁
   * @param route 路由路線
   */
  const handleMobileRedirect = (route: ROUTES) => {
    // 處理導頁
    routerHistory.push(route);
    // 透過 jQuery 關閉手機版 Navigation
    $('#mobile-nav-btn').trigger('click');
  };

  /**
   * @description 取得 Menu 資料
   */
  const getMenuSource = () => {
    switch (routerLocation.pathname) {
      case ROUTES.HOME__TRAVEL_INSURANCE: {
        return HomeTravelInsuranceMenu;
      }
      case ROUTES.HOME__LIFE_INSURANCE: {
        return HomeLifeInsuranceMenu;
      }
      default: {
        return [];
      }
    }
  };

  /**
   * @description 執行會員登出
   * @param device 裝置
   */
  const handleLogout = (device: DeviceTypesEnum) => {
    if (!memberProfileState) return;
    const { USER_ID, userName } = memberProfileState.USERS;
    reduxDispatch(sendLogoutAction({ loginType: LoginTypesEnum.FrontendLogout, USER_ID, userName }));
    // 判斷若為行動裝置，則透過 jQuery 關閉手機版 Navigation
    if (device === DeviceTypesEnum.Mobile) {
      $('#mobile-nav-btn').trigger('click');
    }
  };

  /**
   * @description 處理開啟「保單條款」
   */
  const handleOpenPolicyTerms = () => {
    const docURLParams = commonBaseUrlsState.find((param) => param.paramId === SystemParamIdEnum.DocURL);
    const policyTermsDoc = commonExtDocState?.find((doc) => doc.id === DocsIdEnum.UserManual);
    if (docURLParams && policyTermsDoc) window.open(`${docURLParams.paramValue}${policyTermsDoc.doc}`, '_blank');
  };

  /**
   * @description 處理 mobile-bar-menu 元素點擊的事件
   */
  const handleMobileBarMenuClick = () => {
    const barMenu = $('#bar-menu-items-wrapper');
    if (barMenu) barMenu.slideToggle();
  };

  /**
   * @description 處理手機選單選項展開/收合
   * @param navId navId
   */
  const handleItemExpand = (navId: string) => {
    const navElem = $(navId);
    if (navElem) {
      // 設定 icon 效果
      if (navElem.hasClass('expand')) {
        navElem.addClass('expand');
      } else {
        navElem.removeClass('expand');
      }
      // 展開/收合
      $(`[data-for-item='${navId}']`).slideToggle();
    }
  };

  /**
   * @description 根據 HM1240404E、GN1320200E 準則，組合標題文字
   */
  const getUserMenuTitle = () => {
    let title = '';
    const userManual = commonExtDocState?.find(item => item.id === DocsIdEnum.UserManual);
    if (userManual) {
      title = userManual.doc ? `開啟「${userManual.doc}」` : '';
    }
    return title;
  };

  /**
   * @description 實作 AA 無障礙 Tab 鍵遊走效果(焦點)
   * @param navId 主選單 ID
   */
  const handleSubItemFocus = (navId: string) => {
    const subItems = $(`#${navId}`).find('.sub-items');
    if (subItems) {
      subItems.css('top', '63px');
    }
  };

  /**
   * @description 實作 AA 無障礙 Tab 鍵遊走效果(失焦)
   * @param navId 主選單 ID
   */
  const handleSubItemBlur = (navId: string) => {
    const subItems = $(`#${navId}`).find('.sub-items');
    if (subItems) {
      subItems.css('top', '-300px');
    }
  };

  const handleMemberItemFocus = () => {
    const subItems = $('#greeting').find('.sub-items');
    if (subItems) {
      subItems.css('top', '63px');
    }
  };

  const handleMemberItemBlur = () => {
    const subItems = $('#greeting').find('.sub-items');
    if (subItems) {
      subItems.css('top', '-300px');
    }
  };

  const handlMobileNavClick = () => {
    const isOpen = $('#mobile-nav-btn').attr('data-mobile-nav') === '1';
    setMobileNav(!isOpen);
    reduxDispatch(setIsMobileNavOpenAction(!isOpen));
  };

  /**
   * @description 處理入口錨點 onClick 執行的事件
   * @param event event object
   */
  const handleEntryClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // 跳到中央內容區塊
    const mainContent = $('#main-content');
    if (mainContent) {
      mainContent.focus();
    }
  };

  return (
    <header>
      <nav className={'navbar fixed-top navbar-expand' + (menuBarActive ? ' header-nav-slide-out' : '')}>
        {/* GN1240100E (可導覽 - 跳過區塊) */}
        <a tabIndex={isModalShown ? -1 : 0} style={{ marginLeft: 10 }} href="#main-entry" className="access-brick-white d-none d-lg-block" title="跳到主要內容" onClick={handleEntryClick}>跳到主要內容</a>
        <div className="container">
          <img src={require('assets/img/icons/logo.svg').default} className="logo" alt="" />
          <div className="d-flex ">
            <button tabIndex={isModalShown ? -1 : 0} title={getUserMenuTitle()} type="button" className="navbar-toggler greeting" data-bs-target="#login-modal" data-bs-toggle="modal" aria-label="使用說明" onClick={handleOpenPolicyTerms}>
              <div className="question" />
            </button>
            <button tabIndex={-1} type="button" className="navbar-toggler" data-bs-target="#login-modal" data-bs-toggle="modal" aria-label="登入">
              <img src={require('assets/img/icons/user.svg').default} alt="user" />
            </button>
            <button data-mobile-nav={mobileNav ? '1' : '0'} tabIndex={isModalShown ? -1 : 0} type="button" id="mobile-nav-btn" className="navbar-toggler burger" aria-label="打開側邊選單" onClick={handlMobileNavClick} />
          </div>
          {/* eslint-disable jsx-a11y/no-access-key */}
          {/* 避免畫面閃爍，故設定 preventDefault() */}
          <a tabIndex={isModalShown ? -1 : 0} id="main-nav" href="#main-nav" accessKey="U" className="access-brick-white d-none d-lg-block" title="上方導覽連結區：ALT+U" onClick={(e) => e.preventDefault()}>:::</a>
          <div className="collapse navbar-collapse justify-content-between">
            <ul className="navbar-nav">
              <li className="nav-item-wrapper">
                <Link tabIndex={isModalShown ? -1 : 0} to={ROUTES.HOME__SITE_MAP} className="nav-item">網站導覽</Link>
              </li>
              <li className="nav-item-wrapper">
                <Link tabIndex={isModalShown ? -1 : 0} to={ROUTES.HOME__MAIN} className="nav-item">網投首頁</Link>
              </li>
              {headerState.map((nav) => (
                <li id={nav.id} className="nav-item-wrapper" key={nav.id}>
                  {/* GN1210100E (鍵盤可操作) */}
                  {/* <div className="nav-item" onClick={() => handleNavigation(DeviceTypesEnum.PC, nav)}>{nav.text}</div> */}
                  <a tabIndex={isModalShown ? -1 : 0} href="/#" className="nav-item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNavigation(DeviceTypesEnum.PC, nav); }}>{nav.text}</a>
                  {nav.child && (
                    <div className="sub-items">
                      {nav.child.map((subNav) => (
                        // <div className="item" onClick={() => handleNavigation(DeviceTypesEnum.PC, subNav)} key={subNav.id}>
                        //   {subNav.text}
                        // </div>
                        <a tabIndex={isModalShown ? -1 : 0} id={subNav.id} onBlur={() => handleSubItemBlur(nav.id)} onFocus={() => handleSubItemFocus(nav.id)} href="/#" className="item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNavigation(DeviceTypesEnum.PC, subNav); $(`#${subNav.id}`).blur(); }} key={subNav.id}>{subNav.text}</a>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="d-flex">
              {/* GN1210100E (鍵盤可操作) */}
              <div className="greeting nav-item-wrapper">
                <div className="nav-item pe-0">
                  <div className="question mx-2" />
                  <div className="login-text">
                    <a tabIndex={isModalShown ? -1 : 0} href="/#" title={getUserMenuTitle()} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenPolicyTerms(); }}>
                      <span>{commonExtDocState?.find(item => item.id === DocsIdEnum.UserManual)?.text ?? ''}</span>
                    </a>
                  </div>
                </div>
              </div>

              <div id="greeting" className="greeting nav-item-wrapper">
                <div className="nav-item pe-0">
                  <div className="user mx-2" />
                  <div className="login-text">
                    {!authorizationState
                      ? (
                        <>
                          {/* GN1210100E (鍵盤可操作) */}
                          {/* <span onClick={() => handleOpenLoginModal(DeviceTypesEnum.PC)}>登入</span> */}
                          <a tabIndex={isModalShown ? -1 : 0} href="/#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenLoginModal(DeviceTypesEnum.PC); }}>
                            <span>登入</span>
                          </a>
                          <span> / </span>
                          <Link tabIndex={isModalShown ? -1 : 0} to={ROUTES.MEMBER__REGISTER__AUTH}>
                            <span>註冊</span>
                          </Link>
                        </>
                        )
                      : (
                        <>
                          {memberProfileState && (
                            <>
                              <span>您好，</span>
                              <span>{commonService.protectedName(memberProfileState.USERS.userName)}</span>
                            </>
                          )}
                        </>
                        )}
                  </div>
                </div>
                {authorizationState && (
                  <div className="sub-items">
                    {/* <Link to={ROUTES.MEMBER__PROFILE}>
                      <div className="item">會員中心</div>
                    </Link> */}
                    <a id="memCenter" tabIndex={isModalShown ? -1 : 0} href="/#" onFocus={() => handleMemberItemFocus()} onBlur={() => handleMemberItemBlur()} className="item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); routerHistory.push(ROUTES.MEMBER__PROFILE); $('#memCenter').blur(); }}>會員中心</a>
                    {/* <div className="item" onClick={() => handleLogout(DeviceTypesEnum.PC)}>登出</div> */}
                    <a id="logout" tabIndex={isModalShown ? -1 : 0} href="/#" onFocus={() => handleMemberItemFocus()} onBlur={() => handleMemberItemBlur()} className="item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLogout(DeviceTypesEnum.PC); $('#logout').blur(); }}>登出</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mobile-nav">
          <div className="wrapper d-flex flex-column justify-content-between h-100">
            <ul>
              <li className="link">
                <a href="/#" className="d-flex w-100" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMobileRedirect(ROUTES.HOME__SITE_MAP); }}>
                  網站導覽
                </a>
              </li>
              <li className="link">
                <a href="/#" className="d-flex w-100" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMobileRedirect(ROUTES.HOME__MAIN); }}>
                  網投首頁
                </a>
              </li>
              {headerState.map((nav) => (
                <React.Fragment key={nav.id}>
                  <li id={nav.id} className={'d-flex align-items-center' + (nav.child ? ' has-sub-item' : ' link')}>
                    <a
                      href="/#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (nav.child) {
                          handleItemExpand(nav.id);
                        } else {
                          handleNavigation(DeviceTypesEnum.Mobile, nav);
                        }
                      }}
                    >
                      {nav.text}
                      {nav.child && (
                        <img src={require('assets/img/icons/select-arrow.svg').default} alt="arrow" />
                      )}
                    </a>
                  </li>
                  {nav.child && (
                    <div data-for-item={nav.id}>
                      {nav.child.map((subNav) => (
                        <li className="sub-item link" key={subNav.id}>
                          <a href="/#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNavigation(DeviceTypesEnum.Mobile, subNav); }}>
                            {subNav.text}
                          </a>
                        </li>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {authorizationState && (
                <>
                  <div className="seperation-line" />
                  <li className="link">
                    <a href="/#" className="d-flex w-100" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMobileRedirect(ROUTES.MEMBER__PROFILE); }}>
                      會員中心
                    </a>
                  </li>
                </>
              )}
            </ul>
            {authorizationState
              ? (
                <button type="button" className="btn btn-outline-primary w-100" onClick={() => handleLogout(DeviceTypesEnum.Mobile)}>登出</button>
                )
              : (
                <div className="d-flex flex-column">
                  <button type="button" className="btn btn-primary w-100 mb-2" onClick={() => handleOpenLoginModal(DeviceTypesEnum.Mobile)}>登入</button>
                  <button type="button" className="btn btn-outline-primary w-100" onClick={() => handleMobileRedirect(ROUTES.MEMBER__REGISTER__AUTH)}>註冊</button>
                </div>
                )}
          </div>
          <div className="mobile-nav-backdrop" />
        </div>
      </nav>
      {(routerLocation.pathname === ROUTES.HOME__TRAVEL_INSURANCE || routerLocation.pathname === ROUTES.HOME__LIFE_INSURANCE) && (
        <>
          <div className="menu-bar">
            <div id="menu-bar" className={'container' + (menuBarActive ? ' menu-bar-slide-in' : '')}>
              <ul>
                {/* Header 選單不提供 Tab 鍵遊走，避免干擾滾動效果 */}
                {getMenuSource().map((source) => (
                  <li key={source.target}>
                    <a href="/#" tabIndex={-1} onClick={(event) => commonService.handleWindowScrollToTargetSection(event, source.target)}>
                      {source.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {/* 手機無法以 Tab 鍵遊走，故不處理 */}
          <div className="mobile-menu-bar">
            <div id="mobile-menu-bar" className={'container d-flex align-items-center h-100 justify-content-between' + (menuBarActive ? ' mobile-menu-bar-slide-in' : '')}>
              {getMenuSource().length > 0 && (
                <h2
                  onClick={(event) =>
                    commonService.handleWindowScrollToTargetSection(event, getMenuSource()[0].target)}
                >
                  {sectionActive
                    ? getMenuSource().find((it) => it.target === sectionActive)?.text ?? '商品特色'
                    : '商品特色'}
                </h2>
              )}
              <div className="mobile-menu-container bar-menu">
                <div id="mobile-bar-menu" className="mobile-menu" onClick={handleMobileBarMenuClick}>
                  <div className="current-section">{sectionActive ? getMenuSource().find(it => it.target === sectionActive)?.text ?? '' : '商品特色'}</div>
                </div>
                <div id="bar-menu-items-wrapper" className="menu-items-wrapper">
                  <div className="scroll-box">
                    <ul>
                      {getMenuSource().filter((_, index) => index > 0).map((source) => (
                        <li key={source.target} className={sectionActive === source.target ? 'active' : ''}>
                          <a href="/#" onClick={(event) => commonService.handleWindowScrollToTargetSection(event, source.target)}>
                            {source.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
